import React, { useMemo } from 'react';
import { Ship, Anchor, Sparkles, AlertTriangle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import SupplierCombobox from '@/components/SupplierCombobox';
import LclConceptosTable from './LclConceptosTable.jsx';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN', 'CLP', 'PEN', 'BRL', 'CNY'];

const inputCls =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Umbral bajo el cual conviene que el operador mire el campo. */
const CONFIANZA_BAJA = 0.7;

const Campo = ({ label, children, hint, alerta }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    {alerta && (
      <p className="flex items-center gap-1 text-[11px] text-amber-700">
        <AlertTriangle className="h-3 w-3" /> {alerta}
      </p>
    )}
  </div>
);

const soloFecha = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * Formulario de revisión de una cotización LCL.
 *
 * Es el paso donde el operador confirma lo que interpretó la máquina.
 * Los campos que la extracción marcó con poca confianza se resaltan en
 * ámbar: la idea es dirigir la mirada, no obligar a releer todo.
 */
export default function LclCotizacionForm({
  data = {},
  onChange,
  magnitudes = {},
  catalogo = [],
  readOnly = false,
}) {
  const set = (campo, valor) => onChange?.({ ...data, [campo]: valor });

  const confianza = data.confianzaExtraccion || {};
  const dudoso = (clave) =>
    confianza[clave] !== undefined && confianza[clave] < CONFIANZA_BAJA;

  const origen = data.origenExtraccion || 'manual';
  const etiquetaOrigen = useMemo(
    () =>
      ({
        ia: { texto: 'Leído por IA', clase: 'bg-violet-100 text-violet-800 border-violet-200' },
        mixto: { texto: 'Leído por IA', clase: 'bg-violet-100 text-violet-800 border-violet-200' },
        regex: { texto: 'Lectura básica', clase: 'bg-slate-100 text-slate-700 border-slate-200' },
        manual: { texto: 'Carga manual', clase: 'bg-slate-100 text-slate-700 border-slate-200' },
      })[origen],
    [origen],
  );

  return (
    <div className="space-y-6">
      {/* Cómo se leyó el documento */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={cn('font-normal', etiquetaOrigen?.clase)}>
          <Sparkles className="mr-1 h-3 w-3" />
          {etiquetaOrigen?.texto}
        </Badge>
        {data.procesamientoParcial && (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 font-normal text-amber-800"
          >
            Revisá los datos antes de guardar
          </Badge>
        )}
      </div>

      {data.errorProcesamiento && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{data.errorProcesamiento}</span>
        </div>
      )}

      {/* Identificación */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Identificación</h4>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Campo
            label="Número de cotización"
            alerta={data._numeroGenerado ? 'Se generó uno: el documento no traía número' : null}
          >
            <input
              className={inputCls}
              value={data.numero || ''}
              readOnly={readOnly}
              onChange={(e) => set('numero', e.target.value)}
            />
          </Campo>
          <Campo label="Fecha">
            <input
              type="date"
              className={inputCls}
              value={soloFecha(data.fecha)}
              readOnly={readOnly}
              onChange={(e) => set('fecha', e.target.value || null)}
            />
          </Campo>
          <Campo label="Vigencia hasta">
            <input
              type="date"
              className={inputCls}
              value={soloFecha(data.vigenciaHasta)}
              readOnly={readOnly}
              onChange={(e) => set('vigenciaHasta', e.target.value || null)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Campo label="Proveedor" alerta={dudoso('proveedor') ? 'Confirmá el proveedor' : null}>
            {readOnly ? (
              <input className={inputCls} value={data.proveedorSnapshot?.nombre || ''} readOnly />
            ) : (
              <SupplierCombobox
                value={
                  data.proveedorId
                    ? { _id: data.proveedorId, nombre: data.proveedorSnapshot?.nombre }
                    : null
                }
                defaultQuery={data.proveedorSnapshot?.nombre || ''}
                onChange={(s) => {
                  onChange?.({
                    ...data,
                    proveedorId: s?._id || null,
                    proveedorSnapshot: s
                      ? {
                          id: s._id,
                          nombre: s.nombre,
                          nombreComercial: s.nombreComercial || '',
                          identificacionNumero: s.identificacionNumero || '',
                          identificacionTipo: s.identificacionTipo || '',
                        }
                      : data.proveedorSnapshot,
                  });
                }}
              />
            )}
          </Campo>
          <Campo
            label="Naviera / coloader"
            hint={data.navieraId ? 'Vinculada al catálogo' : 'Texto libre'}
          >
            <input
              className={inputCls}
              value={data.navieraSnapshot?.nombre || data.coloaderNombre || ''}
              readOnly={readOnly}
              onChange={(e) => set('coloaderNombre', e.target.value)}
            />
          </Campo>
          <Campo label="Incoterm">
            <input
              className={inputCls}
              value={data.incotermSnapshot?.codigo || ''}
              readOnly
            />
          </Campo>
        </div>
      </section>

      {/* Monedas */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Moneda y conversión</h4>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Campo label="Moneda de la cotización">
            <select
              className={inputCls}
              value={data.moneda || 'USD'}
              disabled={readOnly}
              onChange={(e) => set('moneda', e.target.value)}
            >
              {MONEDAS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            label="Segunda moneda"
            hint="Cuando el proveedor cotiza algún tramo en otra moneda"
          >
            <select
              className={inputCls}
              value={data.monedaSecundaria || ''}
              disabled={readOnly}
              onChange={(e) => set('monedaSecundaria', e.target.value || null)}
            >
              <option value="">— ninguna —</option>
              {MONEDAS.filter((m) => m !== (data.moneda || 'USD')).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            label={`TRM (${data.monedaSecundaria || '—'} por 1 ${data.moneda || 'USD'})`}
            alerta={
              data.monedaSecundaria && !data.trm
                ? 'Sin TRM esos conceptos no suman al total'
                : null
            }
          >
            <input
              type="number"
              step="any"
              className={inputCls}
              value={data.trm ?? ''}
              placeholder="3219.31"
              readOnly={readOnly || !data.monedaSecundaria}
              onChange={(e) =>
                set('trm', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </Campo>
        </div>
      </section>

      {/* Ruta y servicio */}
      <section className="space-y-3">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold">
          <Anchor className="h-4 w-4" /> Ruta y servicio
        </h4>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Campo
            label="Puerto de origen"
            alerta={dudoso('puerto_origen') ? 'Confirmá el puerto' : null}
          >
            <input
              className={inputCls}
              value={
                [data.origen?.unlocode, data.origen?.ciudad].filter(Boolean).join(' · ') || ''
              }
              readOnly
            />
          </Campo>
          <Campo
            label="Puerto de destino"
            alerta={dudoso('puerto_destino') ? 'Confirmá el puerto' : null}
          >
            <input
              className={inputCls}
              value={
                [data.destino?.unlocode, data.destino?.ciudad].filter(Boolean).join(' · ') || ''
              }
              readOnly
            />
          </Campo>
          <Campo label="Tiempo de tránsito">
            <input
              className={inputCls}
              value={data.tiempoTransito || ''}
              readOnly={readOnly}
              onChange={(e) => set('tiempoTransito', e.target.value)}
            />
          </Campo>
          <Campo label="Días de tránsito">
            <input
              type="number"
              className={inputCls}
              value={data.diasTransito ?? ''}
              readOnly={readOnly}
              onChange={(e) =>
                set('diasTransito', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </Campo>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Campo label="Frecuencia">
            <input
              className={inputCls}
              value={data.frecuencia || ''}
              readOnly={readOnly}
              onChange={(e) => set('frecuencia', e.target.value)}
            />
          </Campo>
          <Campo label="Transbordos">
            <input
              type="number"
              className={inputCls}
              value={data.transbordos ?? ''}
              readOnly={readOnly}
              onChange={(e) =>
                set('transbordos', e.target.value === '' ? null : Number(e.target.value))
              }
            />
          </Campo>
          <Campo label="Días libres en destino" hint="Antes de que corran demoras">
            <input
              type="number"
              className={inputCls}
              value={data.diasLibresDestino ?? ''}
              readOnly={readOnly}
              onChange={(e) =>
                set(
                  'diasLibresDestino',
                  e.target.value === '' ? null : Number(e.target.value),
                )
              }
            />
          </Campo>
          <Campo label="Buque">
            <input
              className={inputCls}
              value={data.nombreBuque || ''}
              readOnly={readOnly}
              onChange={(e) => set('nombreBuque', e.target.value)}
            />
          </Campo>
        </div>
      </section>

      {/* Carga */}
      <section className="space-y-2">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold">
          <Ship className="h-4 w-4" /> Carga de la solicitud
        </h4>
        <div className="flex flex-wrap gap-2 text-xs">
          {[
            ['Bultos', magnitudes.piezas],
            ['Peso', magnitudes.pesoBrutoKg ? `${magnitudes.pesoBrutoKg} kg` : null],
            ['Volumen', magnitudes.volumenM3 ? `${magnitudes.volumenM3} m³` : null],
            ['W/M', magnitudes.unidadesWM],
            [
              'Valor mercancía',
              magnitudes.valorMercancia
                ? `${data.monedaValorMercancia || 'USD'} ${magnitudes.valorMercancia}`
                : null,
            ],
          ]
            .filter(([, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => (
              <Badge key={k} variant="secondary" className="font-normal">
                {k}: {v}
              </Badge>
            ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Son las magnitudes con las que se calculan las bases por W/M, m³ o
          tonelada. Salen de la solicitud, que es la fuente de verdad.
        </p>
      </section>

      {/* Conceptos */}
      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Conceptos y tarifas</h4>
        <LclConceptosTable
          conceptos={data.conceptos || []}
          onChange={(cs) => set('conceptos', cs)}
          magnitudes={magnitudes}
          moneda={data.moneda || 'USD'}
          monedaSecundaria={data.monedaSecundaria}
          trm={data.trm}
          catalogo={catalogo}
          readOnly={readOnly}
        />
      </section>

      {/* Textos */}
      <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Campo label="Observaciones">
          <textarea
            rows={4}
            className={cn(inputCls, 'h-auto py-2')}
            value={data.observaciones || ''}
            readOnly={readOnly}
            onChange={(e) => set('observaciones', e.target.value)}
          />
        </Campo>
        <Campo label="Términos y condiciones">
          <textarea
            rows={4}
            className={cn(inputCls, 'h-auto py-2')}
            value={data.terminos || ''}
            readOnly={readOnly}
            onChange={(e) => set('terminos', e.target.value)}
          />
        </Campo>
      </section>
    </div>
  );
}
